import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useBilling } from '../../../hooks/useBilling'
import styled from 'styled-components'

const Widget = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`

const WidgetTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  color: #666;
`

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const UsageLabel = styled.span`
  color: #666;
`

const UsageValue = styled.span<{ $overLimit?: boolean }>`
  font-weight: 600;
  color: ${props => (props.$overLimit ? '#dc3545' : '#333')};
`

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
`

const ProgressFill = styled.div<{ $percentage: number; $overLimit?: boolean }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props =>
    props.$overLimit
      ? '#dc3545'
      : props.$percentage > 80
      ? '#fd7e14'
      : '#28a745'};
  transition: width 0.3s ease;
`

const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  color: #007bff;
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`

export const UsageWidget: React.FC = () => {
  const navigate = useNavigate()
  const {
    billingData,
    loading,
    getCurrentPlanDetails,
    getUsagePercentage,
    isOverLimit,
  } = useBilling()

  if (loading || !billingData) return null

  const currentPlan = getCurrentPlanDetails()
  const subscription = billingData.subscription

  const mapUsagePercentage = getUsagePercentage(
    billingData.totalMaps,
    currentPlan.limits.maxMaps + (subscription?.additionalMaps || 0),
  )

  const isMapOverLimit = isOverLimit(
    billingData.totalMaps,
    currentPlan.limits.maxMaps + (subscription?.additionalMaps || 0),
  )

  return (
    <Widget>
      <WidgetTitle>Usage This Month</WidgetTitle>

      <UsageRow>
        <UsageLabel>Plan:</UsageLabel>
        <UsageValue>{currentPlan.name}</UsageValue>
      </UsageRow>

      <UsageRow>
        <UsageLabel>Floorplans:</UsageLabel>
        <UsageValue $overLimit={isMapOverLimit}>
          {billingData.totalMaps} /{' '}
          {currentPlan.limits.maxMaps === -1
            ? '∞'
            : currentPlan.limits.maxMaps + (subscription?.additionalMaps || 0)}
        </UsageValue>
      </UsageRow>

      <ProgressBar>
        <ProgressFill
          $percentage={mapUsagePercentage}
          $overLimit={isMapOverLimit}
        />
      </ProgressBar>

      <ViewDetailsLink onClick={() => navigate('/billing')}>
        View billing details →
      </ViewDetailsLink>
    </Widget>
  )
}
